/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Date 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/date/Date.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 10. 15.
 */
import * as parent from '@components/form/field/input/Input';
import { Calendar } from '@elements/calendar/Calendar';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { FormatDate } from '@utils/FormatDate';
import { Language } from '@utils/Language';
import './Date.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 데이터 전송시 날짜 포맷
     */
    format?: string;

    /**
     * 필드에 보일 날짜포맷
     */
    displayFormat?: string;

    /**
     * 직접 날짜를 입력을 할 수 있는지 여부
     */
    editable?: boolean;

    /**
     * 날짜 범위 선택 옵션
     */
    range?: boolean;

    /**
     * 날짜 최소값
     */
    minValue?: string | FormatDate;

    /**
     * 날짜 최댓값
     */
    maxValue?: string | FormatDate;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Date extends parent.Input {
    /**
     * Input 확장타입
     */
    inputVariant = 'date';

    /**
     * 데이터 전송시 날짜 포맷
     */
    format: string;

    /**
     * 필드에 보일 날짜포맷
     */
    displayFormat: string;

    /**
     * 직접 날짜를 입력을 할 수 있는지 여부
     */
    editable: boolean;

    /**
     * 날짜 범위 선택 옵션
     */
    range: boolean;

    /**
     * 날짜 최소값
     */
    minValue: FormatDate | null = null;

    /**
     * 날짜 최댓값
     */
    maxValue: FormatDate | null = null;

    /**
     * 캘린더
     */
    calendar: Calendar = null;

    /**
     * button DOM
     */
    $button: Dom = null;

    /**
     * 범위선택을 위한 임시저장
     */
    rangeValue: { start: FormatDate; end: FormatDate } = null;

    /**
     * Date 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Date> = null) {
        super(properties);

        this.format = this.properties?.format ?? 'Y-m-d';
        this.displayFormat = this.properties?.displayFormat ?? 'Y-m-d';
        this.editable = this.properties?.editable !== false;
        this.range = this.properties?.range === true;
        this.placeholder = this.properties?.placeholder ?? (this.range == true ? '' : 'YYYY-MM-DD');
        const minValue = this.properties?.minValue ?? null;
        const maxValue = this.properties?.maxValue ?? null;
        this.minValue = minValue instanceof FormatDate ? minValue : minValue !== null ? new FormatDate(minValue) : null;
        this.maxValue = maxValue instanceof FormatDate ? maxValue : maxValue !== null ? new FormatDate(maxValue) : null;
        this.keyboard = true;

        if (this.range === true) {
            this.editable = false;
        }
    }

    /**
     * INPUT 필드 DOM 을 가져온다.
     *
     * @returns INPUT 필드 DOM
     */
    $getInput(): Dom<HTMLInputElement> {
        if (this.$input === null) {
            this.$input = super.$getInput(false, 'text');

            if (this.range === true) {
                this.$input.setAttr('readonly', 'readonly');
                this.$input.on('focus', () => {
                    this.expand();
                });
            } else {
                if (this.editable == true) {
                    this.$input.on('focus', (me) => {
                        if (this.readonly === true) return;

                        me.getEl().placeholder = 'YYYY-MM-DD';
                        if (this.value !== null) {
                            me.setValue(this.value.format('Y-m-d'));
                        }
                    });

                    this.$input.on('input', async (me) => {
                        if (this.readonly === true) return;

                        const value = me.getValue() ?? '';
                        if (value.length > 0) {
                            const date = new FormatDate(me.getValue());
                            if (date.isValid() == true && this.isInRange(date) == true) {
                                await this.setValue(value, false, false);
                                this.updateValidation({ valid: true });
                            } else {
                                this.updateValidation({
                                    valid: false,
                                    message: await Language.getError('INVALID_DATE').getText(),
                                });
                            }
                        } else {
                            this.updateValidation({
                                valid: false,
                                message: await Language.getError('INVALID_DATE').getText(),
                            });
                        }
                    });

                    this.$input.on('blur', async (me) => {
                        console.log('blur');
                        if (this.readonly === true) return;

                        me.getEl().placeholder = await Language.resolveText(this.placeholder);
                        const value = me.getValue() ?? '';
                        if (value.length > 0) {
                            const date = new FormatDate(value);
                            if (date.isValid() == true && this.isInRange(date) == true) {
                                await this.setValue(value, false, true);
                                await this.isValid(value);
                            } else {
                                this.updateValidation({
                                    valid: false,
                                    message: await Language.getError('INVALID_DATE').getText(),
                                });
                            }
                        } else {
                            await this.setValue(null, false, false);
                            await this.isValid(value);
                        }
                    });

                    this.$input.on('keydown', (me, e) => {
                        if (this.readonly === true) return;

                        const value = me.getValue() ?? '';
                        if (value.search(/^[0-9]{4}\-[0-9]{2}\-[0-9]{2}$/) === 0) {
                            const date = new FormatDate(value);
                            if (date.isValid() == true) {
                                if (e.key == 'ArrowUp' || e.key == 'ArrowDown') {
                                    date.addDate(e.key == 'ArrowUp' ? -1 : 1);
                                    if (this.isInRange(date) == true) {
                                        me.setValue(date.format('Y-m-d'));
                                        this.setValue(date, false, false);
                                    }
                                    e.preventDefault();
                                }
                            }
                        }

                        if (e.key != 'Escape') {
                            e.stopImmediatePropagation();
                        }
                    });
                }
            }
        }

        return this.$input;
    }

    /**
     * 버튼 DOM 을 가져온다.
     *
     * @returns 버튼 DOM
     */
    $getButton(): Dom {
        if (this.$button === null) {
            this.$button = Html.create('button', {
                type: 'button',
                'data-action': 'calendar',
            });

            this.$button.on('pointerdown', () => {
                this.toggle();
            });
        }

        return this.$button;
    }

    /**
     * 달력 컴포넌트를 가져온다.
     *
     * @returns 달력 컴포넌트
     */
    getCalendar(): Calendar {
        if (this.calendar === null) {
            const value = this.getRawValue();
            this.calendar = new Calendar({
                header: true,
                footer: true,
                current: value?.start ?? value ?? null,
                start: this.minValue ?? null,
                end: this.maxValue ?? null,
                listeners: {
                    click: async (date, $date) => {
                        if (this.range === true) {
                            this.rangeValue ??= this.getRawValue() ?? { start: null, end: null };

                            /**
                             * 범위가 이미 선택완료된 상태인 경우, 기존 선택범위를 초기화한다.
                             */
                            if (this.rangeValue.start !== null && this.rangeValue.end !== null) {
                                this.markOnCalendar({ start: null, end: null });
                                this.rangeValue = { start: null, end: null };
                            }

                            if (this.rangeValue.start === null) {
                                this.rangeValue.start = date;
                                $date.addClass('selected');
                                this.$getInput().setValue(this.rangeValue.start.format(this.displayFormat) + ' ~ ');
                                return;
                            }

                            if (date.getTime() < this.rangeValue.start.getTime()) {
                                this.rangeValue.end = this.rangeValue.start;
                                this.rangeValue.start = date;
                                await this.setValue(this.rangeValue, false, true);
                                this.markOnCalendar();
                            } else if (this.rangeValue.start.getTime() <= date.getTime()) {
                                this.rangeValue.end = date;
                                await this.setValue(this.rangeValue, false, true);
                                this.markOnCalendar();
                            }
                        } else {
                            await this.setValue(date, false, true);
                            this.updateValidation({ valid: true });
                            await this.collapse();
                        }

                        this.focus();
                    },
                    hover: async (date) => {
                        if (this.range === true) {
                            this.rangeValue ??= this.getRawValue() ?? { start: null, end: null };

                            /**
                             * 범위를 선택중이라면, 마우스오버시 선택범위를 미리 보여준다.
                             */
                            if (this.rangeValue.start !== null && this.rangeValue.end === null) {
                                Html.all('li.range', this.getCalendar().$getDates()).removeClass('range');
                                this.getCalendar().$getRange(this.rangeValue.start, date).addClass('range');
                                this.getCalendar()
                                    .$getDate(this.rangeValue.start)
                                    ?.removeClass('range')
                                    .addClass('selected');
                            }
                        }
                    },
                    change: () => {
                        this.markOnCalendar();
                    },
                },
            });

            this.calendar
                .$getElement()
                .setAttr('popover', 'manual')
                .setAttr('data-popover', `${this.getId()}-calendar`)
                .on('toggle', async (_, e: ToggleEvent) => {
                    if (e.newState === 'closed') {
                        await this.collapse();
                    } else {
                        await this.expand();
                    }
                });

            this.calendar.setComponent(this);
        }

        return this.calendar;
    }

    /**
     * 캘린더를 렌더링한다.
     */
    async renderCalendar(): Promise<void> {
        this.$getComponent().append(this.getCalendar().$getElement());

        await this.getCalendar().render();
    }

    /**
     * 캘린더를 표시한다.
     */
    async expand(): Promise<void> {
        if (this.isRendered() == false) {
            return;
        }

        if (this.readonly === true) {
            return;
        }

        if (this.isDisabled() === true) {
            return;
        }

        if (this.getCalendar().isRendered() === false) {
            await this.renderCalendar();
        }

        this.getCalendar().$getElement().getEl().showPopover();

        this.$getContainer().addClass('expand');

        const value = this.getRawValue();

        if (this.range == true) {
            await this.getCalendar().setCurrent(value?.start ?? null);
        } else {
            await this.getCalendar().setCurrent(value ?? null);
        }

        this.markOnCalendar();
    }

    /**
     * 캘린더를 숨긴다.
     */
    async collapse(): Promise<void> {
        if (this.isExpand() == true) {
            this.getCalendar().$getElement().getEl().hidePopover();
        }

        this.$getContainer().removeClass('expand');

        if (this.range == true) {
            this.rangeValue ??= this.getRawValue() ?? { start: null, end: null };
            if (this.rangeValue.start === null || this.rangeValue.end === null) {
                this.rangeValue = this.getRawValue();
                if (this.rangeValue !== null && this.rangeValue.start !== null && this.rangeValue.end !== null) {
                    this.$getInput().setValue(
                        this.rangeValue.start.format(this.displayFormat) +
                            ' ~ ' +
                            this.rangeValue.end.format(this.displayFormat)
                    );
                } else {
                    await this.setValue(null);
                }
            }
        }
    }

    /**
     * 캘린더를 표시하거나 숨긴다.
     */
    async toggle(): Promise<void> {
        if (this.isExpand() == true) {
            await this.collapse();
        } else {
            await this.expand();
        }
    }

    /**
     * 캘린더가 보이는 상태인지 확인한다.
     *
     * @returns 캘린더가 보이는지 여부
     */
    isExpand(): boolean {
        if (this.calendar === null || this.calendar.isRendered() === false) {
            return false;
        }
        return this.getCalendar().$getElement().getEl().matches(':popover-open');
    }

    /**
     * 캘린더에 현재 값을 마킹한다.
     *
     * @param value - 현재 필드값이 아닌 특정값으로 마킹할 경우
     */
    markOnCalendar(value: any = null): void {
        value ??= this.getRawValue();
        if (this.range == true) {
            this.getCalendar().$getRange().removeClass('selected', 'range');
            if (value !== null && value.start !== null && value.end !== null) {
                this.getCalendar().$getRange(value.start, value.end).addClass('range');
                this.getCalendar().$getDate(value.start)?.removeClass('range').addClass('selected');
                this.getCalendar().$getDate(value.end)?.removeClass('range').addClass('selected');
            }
        } else {
            if (value !== null) {
                this.getCalendar().$getRange().removeClass('selected', 'range');
                this.getCalendar().$getDate(value)?.addClass('selected');
            }
        }
    }

    /**
     * 날짜가 minValue ~ maxValue 범위 안에 있는지 확인한다.
     *
     * @param date - 검사할 날짜
     * @returns 범위 내 여부
     */
    isInRange(date: FormatDate): boolean {
        if (this.minValue !== null && date.getTime() < this.minValue.getTime()) {
            return false;
        }
        if (this.maxValue !== null && date.getTime() > this.maxValue.getTime()) {
            return false;
        }
        return true;
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     * @param is_update - INPUT 태그의 값을 변경할 지 여부
     */
    async setValue(value: any = null, is_init: boolean = false, is_update: boolean = true): Promise<void> {
        await this.lockValue();

        if (this.range == true) {
            if (typeof value !== 'object' || value?.start === undefined || value?.end === undefined) {
                value = { start: null, end: null };
            }

            if (value.start instanceof FormatDate) {
                value.start = value.start.clone();
            } else if (value.start !== null) {
                value.start = new FormatDate(value.start);
            }

            if (value.end instanceof FormatDate) {
                value.end = value.end.clone();
            } else if (value.end !== null) {
                value.end = new FormatDate(value.end);
            }

            if (value?.start?.isValid() !== true || value?.end?.isValid() !== true) {
                value = null;
            }
        } else {
            if (value instanceof FormatDate) {
                value = value.clone();
            } else if (value !== null) {
                value = new FormatDate(value);
            }

            if (value?.isValid() !== true) {
                value = null;
            }
        }

        if (is_update == true) {
            if (value === null) {
                this.$getInput().setValue('');
            } else {
                if (this.range == true) {
                    this.$getInput().setValue(
                        value.start.format(this.displayFormat) + ' ~ ' + value.end.format(this.displayFormat)
                    );
                } else {
                    this.$getInput().setValue(value.format(this.displayFormat));
                }
            }
        }

        this.rangeValue = null;
        await this.releaseValue(value, is_init);
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<any> {
        if (this.isRendered() == false) {
            await this.render(true);
        }

        if (this.value === null) {
            return null;
        }

        if (this.range == true) {
            return { start: this.value.start.format(this.format), end: this.value.end.format(this.format) };
        } else {
            return this.value.format(this.format);
        }
    }

    /**
     * 현재 값인 FormatDate 객체를 그대로 가져온다.
     *
     * @returns RAW 데이터
     */
    getRawValue(): any {
        return this.value;
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        this.collapse();
        this.$getButton().setDisabled(disabled);
        return super.setDisabled(disabled);
    }

    /**
     * 필드값이 유효한지 확인한다.
     *
     * @param value - 유효성결과를 검증할 값 (정의되지 않은 경우, 현재 값)
     * @returns 필드값 유효성결과
     */
    protected async validate(value: any = undefined): Promise<{ valid: boolean; message?: string }> {
        if (this.isDisabled() === true) {
            return { valid: true };
        }

        if (value === undefined) {
            value = await this.getValue();
        }

        let validation = this.getValidationHistory(value);
        if (validation === null) {
            validation = { valid: true };

            const isBlank = await this.isBlank(value);
            const isInvalidDate =
                this.range == true
                    ? value?.start === undefined ||
                      value?.end === undefined ||
                      new FormatDate(value.start).isValid() == false ||
                      new FormatDate(value.end).isValid() == false
                    : new FormatDate(value).isValid() == false;

            if (this.allowBlank === false && isBlank == true) {
                validation.valid = false;
                validation.message = await Language.getError('REQUIRED').getText();
            } else if (isBlank == false && isInvalidDate == true) {
                validation.valid = false;
                validation.message = await Language.getError('INVALID_DATE').getText();
            } else if (typeof this.validator == 'function') {
                const results = await this.validator(value, this);
                if (typeof results == 'boolean') {
                    validation.valid = results;
                } else {
                    validation.valid = false;
                    validation.message = results;
                }
            }
        }

        return validation;
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        await super.renderField();
        this.$getContent().append(this.$getButton());
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        await super.renderState();
        this.$getContent().setAttr('data-popover', `${this.getId()}-calendar`);
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        this.collapse();
        this.calendar?.remove(false);
        this.calendar = null;
        this.$button = null;

        await super.remove(removeDom);
    }

    /**
     * 컴포넌트 DOM 이 연결해제되었을 때 이벤트를 처리한다.
     */
    async onDisconnect(): Promise<void> {
        this.collapse();
        await super.onDisconnect();
    }

    /**
     * 포커스 해제시 이벤트를 처리한다.
     *
     * @returns 상위 컴포넌트로 전파할지 여부 (false 인 경우 전파하지 않는다.)
     */
    async onBlur(): Promise<boolean> {
        this.collapse();
        await this.fireEvent('blur', [this]);
        return true;
    }
}
